import { useEffect, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { myobExportApi, type MyobExportConfig } from "@/lib/myob-export";
import { ErrorState } from "@/components/error-state";
import { LoadingState } from "@/components/loading-state";
import { PageContainer } from "@/app/layouts/PageContainer";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { toast } from "@/hooks/use-toast";
import { PageHeader } from "@/components/page-header";

const FIELDS: { key: keyof MyobExportConfig; label: string }[] = [
  { key: "salesAccount", label: "Sales account" },
  { key: "tradeDebtorsAccount", label: "Trade debtors account" },
  { key: "bankAccount", label: "Bank account" },
  { key: "gstTaxCode", label: "GST tax code" },
  { key: "freeTaxCode", label: "Non-taxable code" },
  { key: "journalNumberPrefix", label: "Journal number prefix (optional)" },
];

export function MyobExportConfigPage() {
  const qc = useQueryClient();
  const query = useQuery({ queryKey: ["myob-config"], queryFn: myobExportApi.getConfig });
  const [form, setForm] = useState<MyobExportConfig | null>(null);

  useEffect(() => {
    if (query.data && !form) setForm(query.data);
  }, [query.data, form]);

  const save = useMutation({
    mutationFn: (cfg: MyobExportConfig) => myobExportApi.putConfig(cfg),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["myob-config"] });
      toast.success("Saved");
    },
    onError: () => {
      toast.error("Save failed. Please try again.");
    },
  });

  if (query.isError) return <ErrorState onRetry={() => void query.refetch()} />;
  if (query.isLoading || !form) return <LoadingState />;

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (form) save.mutate(form);
  }

  return (
    <PageContainer>
      <PageHeader title="MYOB Export Configuration" />
      <form onSubmit={handleSubmit} className="max-w-lg space-y-6">
        <div>
          <p className="mt-1 text-sm text-muted-foreground">
            GL account numbers and tax codes written into the MYOB import file. Match these to your
            MYOB chart of accounts.
          </p>
        </div>
        <div className="flex flex-col gap-3">
          {FIELDS.map((f) => (
            <div key={f.key}>
              <Label htmlFor={f.key}>{f.label}</Label>
              <Input
                id={f.key}
                value={form[f.key]}
                onChange={(e) => setForm({ ...form, [f.key]: e.target.value })}
              />
            </div>
          ))}
        </div>
        <div className="flex items-center gap-3">
          <Button type="submit" disabled={save.isPending}>
            {save.isPending ? "Saving…" : "Save"}
          </Button>
        </div>
      </form>
    </PageContainer>
  );
}
